import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  X, Plus, Database, ChevronDown, ChevronRight,
  PlayCircle, Link2, Settings, Trash2,
  FilePlus, RefreshCw, ShieldCheck, PauseCircle,
  CheckCircle2, XCircle, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { FieldRow } from './field-row';
import { getDefaultDummyEmail, DUMMY_EMAIL_DOMAIN_SUFFIX } from '../default-field-mappings';
import { FieldAddDialog } from './field-add-dialog';
import { PicklistMappingDialog } from './picklist-mapping-dialog';
import { AdvancedFormatDialog } from './advanced-format-dialog';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useModuleStore } from '@/stores/module-store';
import { useFieldMapping } from '@/hooks/use-field-mapping';
import { useRecentEvents } from '../hooks/use-recent-events';
import { useEventFieldPaths } from '../hooks/use-event-field-paths';
import { MMM_ALLOWING_SAAS, MMM_DISALLOWING_DIRECT_UPDATE } from '@/types/field-mapping-flow';
import { EVENT_APPS, getSaaSPrettyName, getTPADisplayName } from '@/config/constants';
import { useAppStore } from '@/stores/app-store';
import { useSaaSModule } from '@/hooks/use-saas-module';
import { toast } from 'sonner';
import type { MMMCard, PicklistMapping, FieldFormatConfig } from '@/types/field-mapping-flow';
import type { CRMField } from '@/types/saas';

// ── Test Result Types ──

interface FieldResult {
  fieldApiName: string;
  fieldLabel: string;
  required: boolean;
  mappedValue: string;
  resolvedValue: string;
  status: 'ok' | 'error' | 'empty';
  issue?: string;
}

interface ApiResult {
  attempted: boolean;
  success: boolean;
  recordId?: string;
  recordUrl?: string;
  error?: string;
  requestPayload?: Record<string, unknown>;
  response?: Record<string, unknown>;
}

interface ValidationResult {
  passed: boolean;
  totalFields: number;
  resolvedCount: number;
  errorCount: number;
  fields: FieldResult[];
}

interface FormTestResult {
  mode: 'create' | 'update';
  label: string;
  validation: ValidationResult;
  api: ApiResult | null;
  needsRecordUrl?: boolean;
}

interface TestResultData {
  forms: FormTestResult[];
  overallPassed: boolean;
}

type MappingMode = 'create' | 'update';

/**
 * Human label for a requiredOneOf group, e.g. "Person or Organization".
 * Falls back to api names so an unlabelled group is still identifiable.
 */
function relationGroupLabel(group: { fields: CRMField[] }): string {
  return group.fields.map((f) => f.displayLabel || f.apiName).join(' or ');
}

// ── Per-Form Test Result Section Component ──

interface FormTestResultSectionProps {
  form: FormTestResult;
  /** When the update API was skipped because no record URL was provided */
  needsRecordUrl?: boolean;
  recordUrl?: string;
  onRecordUrlChange?: (v: string) => void;
  onRetry?: () => void;
  retrying?: boolean;
}

function FormTestResultSection({
  form, needsRecordUrl, recordUrl, onRecordUrlChange, onRetry, retrying,
}: FormTestResultSectionProps) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [responseOpen, setResponseOpen] = useState(true);
  const [requestOpen, setRequestOpen] = useState(true);

  const overallSuccess = form.validation.passed && (form.api === null || form.api.success);
  const ModeIcon = form.mode === 'create' ? FilePlus : RefreshCw;

  return (
    <div className="border-t border-border/50">
      {/* Form Header */}
      <button
        onClick={() => setSectionOpen(!sectionOpen)}
        className="flex w-full items-center gap-1.5 px-4 py-2 text-left hover:bg-muted/30 transition-colors"
      >
        <ChevronDown className={`h-2.5 w-2.5 text-muted-foreground transition-transform ${sectionOpen ? '' : '-rotate-90'}`} />
        <ModeIcon className={`h-3 w-3 shrink-0 ${overallSuccess ? 'text-emerald-500' : needsRecordUrl ? 'text-amber-500' : 'text-destructive'}`} />
        <span className="text-[10px] font-semibold text-foreground flex-1">{form.label}</span>
        {overallSuccess ? (
          <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
        ) : needsRecordUrl ? (
          <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
        ) : (
          <XCircle className="h-3 w-3 text-destructive shrink-0" />
        )}
      </button>

      {sectionOpen && (
        <div className="px-4 pb-2 space-y-1.5">
          {/* Validation summary */}
          <div className="flex items-center gap-1.5">
            {form.validation.passed ? (
              <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />
            ) : (
              <AlertTriangle className="h-2.5 w-2.5 text-amber-500 shrink-0" />
            )}
            <span className="text-[9px] text-foreground">
              <span className="font-medium">Validation:</span>{' '}
              {form.validation.passed
                ? `${form.validation.resolvedCount}/${form.validation.totalFields} fields resolved`
                : `${form.validation.errorCount} error${form.validation.errorCount !== 1 ? 's' : ''} found`
              }
            </span>
          </div>

          {/* API result summary */}
          {form.api && !needsRecordUrl && (
            <div className="flex items-center gap-1.5">
              {form.api.success ? (
                <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />
              ) : (
                <XCircle className="h-2.5 w-2.5 text-destructive shrink-0" />
              )}
              <span className="text-[9px] text-foreground flex-1 min-w-0">
                <span className="font-medium">API:</span>{' '}
                {form.api.success ? (
                  <>
                    {form.mode === 'create' ? 'Record created' : 'Record updated'}
                    {form.api.recordId && (
                      <span className="ml-1 font-mono text-[8px] text-muted-foreground">
                        ({form.api.recordId})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-destructive">{form.api.error}</span>
                )}
              </span>
              {form.api.recordUrl && (
                <a
                  href={form.api.recordUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded p-0.5 text-primary hover:bg-primary/10 transition-colors"
                  title="Open record in CRM"
                >
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
          )}

          {/* Record URL prompt — shown when update API needs a target record */}
          {needsRecordUrl && (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 dark:border-amber-500/20 dark:bg-amber-500/5 p-2 space-y-1.5">
              <p className="text-[9px] text-foreground leading-relaxed">
                <span className="font-medium">Record URL needed</span> — Open an existing record in your CRM browser tab and paste its URL here to test the update.
              </p>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={recordUrl || ''}
                  onChange={(e) => onRecordUrlChange?.(e.target.value)}
                  placeholder="https://your-crm.com/record/12345"
                  className="flex-1 rounded border border-border bg-background px-2 py-1 text-[10px] font-mono text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  onClick={onRetry}
                  disabled={!recordUrl?.trim() || retrying}
                  className="shrink-0 rounded-md bg-primary px-2 py-1 text-[9px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40 disabled:pointer-events-none"
                >
                  {retrying ? 'Testing…' : 'Retry'}
                </button>
              </div>
            </div>
          )}

          {/* Field Details (expandable) */}
          {form.validation.fields.length > 0 && (
            <div className="rounded-md border border-border/50 overflow-hidden">
              <button
                onClick={() => setFieldsOpen(!fieldsOpen)}
                className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left hover:bg-muted/30 transition-colors bg-muted/20"
              >
                <ChevronDown className={`h-2 w-2 text-muted-foreground transition-transform ${fieldsOpen ? '' : '-rotate-90'}`} />
                <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Fields ({form.validation.fields.length})
                </span>
              </button>
              {fieldsOpen && (
                <div className="max-h-[140px] overflow-y-auto px-2.5 py-1 scrollbar-thin">
                  <div className="space-y-0.5">
                    {form.validation.fields.map((field) => (
                      <div
                        key={field.fieldApiName}
                        className="flex items-start gap-1 text-[9px]"
                      >
                        {field.status === 'ok' ? (
                          <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0 mt-px" />
                        ) : field.status === 'error' ? (
                          <XCircle className="h-2.5 w-2.5 text-destructive shrink-0 mt-px" />
                        ) : (
                          <div className="h-2.5 w-2.5 shrink-0 mt-px rounded-full border border-border" />
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-foreground">{field.fieldLabel}</span>
                          {field.required && <span className="text-[7px] text-destructive font-bold ml-0.5">*</span>}
                          {field.status === 'ok' && field.resolvedValue && (
                            <span className="ml-1 font-mono text-[8px] text-muted-foreground">
                              {/* Show payload value if available and different from resolved */}
                              {form.api?.requestPayload?.[field.fieldApiName] != null &&
                               String(form.api.requestPayload[field.fieldApiName]) !== field.resolvedValue
                                ? String(form.api.requestPayload[field.fieldApiName])
                                : field.resolvedValue
                              }
                            </span>
                          )}
                          {field.issue && (
                            <span className="ml-1 text-[8px] text-destructive">{field.issue}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* API Response JSON (expandable) */}
          {form.api?.response && (
            <div className="rounded-md border border-border/50 overflow-hidden">
              <button
                onClick={() => setResponseOpen(!responseOpen)}
                className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left hover:bg-muted/30 transition-colors bg-muted/20"
              >
                <ChevronDown className={`h-2 w-2 text-muted-foreground transition-transform ${responseOpen ? '' : '-rotate-90'}`} />
                <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                  API Response
                </span>
              </button>
              {responseOpen && (
                <div className="px-2.5 py-1">
                  <pre className="max-h-[160px] overflow-auto rounded border border-border bg-muted/40 p-1.5 text-[8px] font-mono text-foreground leading-relaxed scrollbar-thin">
                    {JSON.stringify(form.api.response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Request Payload JSON (expandable) */}
          {form.api?.requestPayload && (
            <div className="rounded-md border border-border/50 overflow-hidden">
              <button
                onClick={() => setRequestOpen(!requestOpen)}
                className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left hover:bg-muted/30 transition-colors bg-muted/20"
              >
                <ChevronDown className={`h-2 w-2 text-muted-foreground transition-transform ${requestOpen ? '' : '-rotate-90'}`} />
                <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Request Payload
                </span>
              </button>
              {requestOpen && (
                <div className="px-2.5 py-1">
                  <pre className="max-h-[160px] overflow-auto rounded border border-border bg-muted/40 p-1.5 text-[8px] font-mono text-foreground leading-relaxed scrollbar-thin">
                    {JSON.stringify(form.api.requestPayload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Field Mapping Panel — slide-out panel from right.
 *
 * Layout:
 *   1. Header (card info + settings gear popover)
 *   2. How it works banner
 *   3. Create fields — collapsible, optional
 *   4. Update fields — collapsible, optional
 *   5. Footer (test + save)
 *
 * Module Settings (always create new, pause update, link modules)
 * live in a popover triggered by the gear icon in the header.
 */

/**
 * Records created by "Test" runs this app session, keyed by
 * `${branchId}:${cardId}`. When a card links to a previous module
 * (advanced_link_parent), its test reuses the parent record already created
 * by a test this session instead of creating a fresh parent every time.
 */
const testCreatedRecords: Record<string, { module: string; recordId: string }> = {};

export function FieldMappingPanel() {
  const fieldMappingPanelOpen = useFlowBuilderStore((s) => s.fieldMappingPanelOpen);
  const setFieldMappingPanelOpen = useFlowBuilderStore((s) => s.setFieldMappingPanelOpen);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const selectedCardId = useFlowBuilderStore((s) => s.selectedCardId);
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const updateCard = useFlowBuilderStore((s) => s.updateCard);
  const saveBranch = useFlowBuilderStore((s) => s.saveBranch);
  const saving = useFlowBuilderStore((s) => s.saving);
  const addCard = useFlowBuilderStore((s) => s.addCard);
  const selectCard = useFlowBuilderStore((s) => s.selectCard);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);
  const isMultiModule = !!appCodeSAAS && (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS);
  const { module: saasModuleHook } = useSaaSModule();

  // Get the current card
  const currentMapping = selectedBranchId ? fieldMappings[selectedBranchId] : null;
  const currentCard = (() => {
    if (!currentMapping?.fields || !selectedCardId) return null;
    const fields = currentMapping.fields as Record<string, MMMCard>;
    return fields[selectedCardId] || null;
  })();

  // Get all cards for link parent/child options
  const allCards = useMemo(() => {
    if (!currentMapping?.fields) return [];
    return Object.values(currentMapping.fields as Record<string, MMMCard>)
      .filter((v): v is MMMCard => typeof v === 'object' && v !== null && 'cardId' in v)
      .sort((a, b) => (a.orderOf ?? 0) - (b.orderOf ?? 0));
  }, [currentMapping]);

  // Compute card step index
  const cardStepIndex = useMemo(() => {
    if (!selectedCardId) return 0;
    return allCards.findIndex((c) => c.cardId === selectedCardId) + 1;
  }, [allCards, selectedCardId]);

  // CRM fields for the selected module
  const { crmFields, loading: fieldsLoading } = useFieldMapping(
    currentCard?.saasFieldModuleName
  );

  // Reconcile fields_type with actual CRM field data types once CRM fields load.
  // Default schema pre-population can't know CRM data types, so fields_type may
  // be empty or stale — sync it here so saves always have correct types.
  useEffect(() => {
    if (!crmFields.length || !currentCard || !selectedBranchId || !selectedCardId) return;

    const reconcile = (
      fieldsMap: Record<string, string> | undefined,
      fieldsType: Record<string, string> | undefined,
      mode: 'create' | 'update'
    ) => {
      if (!fieldsMap) return null;
      const keys = Object.keys(fieldsMap);
      if (keys.length === 0) return null;

      let changed = false;
      const updated = { ...fieldsType };
      for (const key of keys) {
        const crm = crmFields.find((f) => f.apiName === key);
        const expectedType = crm?.dataType || 'text';
        if (updated[key] !== expectedType) {
          updated[key] = expectedType;
          changed = true;
        }
      }
      return changed ? { mode, fieldsType: updated } : null;
    };

    const createResult = reconcile(
      currentCard.fields as Record<string, string>,
      currentCard.fields_type,
      'create'
    );
    const updateResult = reconcile(
      currentCard.fieldsForExisting?.fields,
      currentCard.fieldsForExisting?.fields_type,
      'update'
    );

    if (createResult || updateResult) {
      const patch: Partial<MMMCard> = {};
      if (createResult) {
        patch.fields_type = createResult.fieldsType;
      }
      if (updateResult) {
        patch.fieldsForExisting = {
          ...currentCard.fieldsForExisting,
          fields_type: updateResult.fieldsType,
        };
      }
      updateCard(selectedBranchId, selectedCardId, patch);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmFields, selectedCardId]);

  // Recent events for field paths
  const { selectedEventData } = useRecentEvents();
  const eventFieldPaths = useEventFieldPaths(selectedEventData);

  // Local state — both sections independently collapsible, both optional
  const [createSectionOpen, setCreateSectionOpen] = useState(() => {
    return Object.keys((currentCard?.fields as Record<string, string>) || {}).length > 0;
  });
  const [createOptionalCollapsed, setCreateOptionalCollapsed] = useState(false);
  const [updateSectionOpen, setUpdateSectionOpen] = useState(() => {
    // Only auto-expand Update if Create has regular (non-match) fields configured
    const createKeys = Object.keys((currentCard?.fields as Record<string, string>) || {});
    const hasCreateRegularFields = createKeys.some((k) => !k.startsWith('UA_INCOMING_'));
    if (!hasCreateRegularFields) return false; // Create must be configured first
    return (currentCard?.fieldsForExisting &&
      Object.keys(currentCard.fieldsForExisting.fields || {}).length > 0) || false;
  });
  const [updateOptionalCollapsed, setUpdateOptionalCollapsed] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const settingsAnimTimer = useRef<ReturnType<typeof setTimeout>>();
  const settingsPopoverRef = useRef<HTMLDivElement>(null);

  const openSettings = useCallback(() => {
    if (settingsAnimTimer.current) clearTimeout(settingsAnimTimer.current);
    setSettingsOpen(true);
    requestAnimationFrame(() => setSettingsVisible(true));
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsVisible(false);
    if (settingsAnimTimer.current) clearTimeout(settingsAnimTimer.current);
    settingsAnimTimer.current = setTimeout(() => setSettingsOpen(false), 180);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (settingsAnimTimer.current) clearTimeout(settingsAnimTimer.current);
  }, []);

  // Click-outside to dismiss settings popover
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Ignore clicks on Select portal dropdowns (rendered via portal to body)
      if (target.closest?.('[data-select-portal]')) return;
      if (settingsPopoverRef.current && !settingsPopoverRef.current.contains(target)) {
        closeSettings();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [settingsOpen, closeSettings]);

  // Dialog state
  const [dialogMode, setDialogMode] = useState<MappingMode>('create');
  const [showFieldAddDialog, setShowFieldAddDialog] = useState(false);
  const [picklistDialogField, setPicklistDialogField] = useState<CRMField | null>(null);
  const [advancedDialogField, setAdvancedDialogField] = useState<string | null>(null);

  const [testLoading, setTestLoading] = useState(false);
  const [testResultData, setTestResultData] = useState<TestResultData | null>(null);
  const [testResultOpen, setTestResultOpen] = useState(false);
  const [testRecordUrl, setTestRecordUrl] = useState('');
  const saasModule = useModuleStore((s) => s.saasModule);

  // Note: MMM_DISALLOWING_DIRECT_UPDATE is defined in the original app but never actually used
  // to hide the Update section — both Create and Update tabs are always shown for Monday.
  // Keeping the constant for reference but not using it to disable the Update form.
  const _disallowDirectUpdate = appCodeSAAS
    ? (MMM_DISALLOWING_DIRECT_UPDATE as readonly string[]).includes(appCodeSAAS)
    : false;
  void _disallowDirectUpdate; // suppress unused warning

  // ── Data access ──

  const createFieldsMap = useMemo(
    () => (currentCard?.fields as Record<string, string>) || {},
    [currentCard?.fields],
  );
  const createFieldsType = currentCard?.fields_type || {};
  const createFieldFormats = currentCard?.fields_formats || {};
  const createPicklistMappings = currentCard?.picklistMapping || {};

  const updateFieldsMap = useMemo(
    () => currentCard?.fieldsForExisting?.fields || {},
    [currentCard?.fieldsForExisting?.fields],
  );
  const updateFieldsType = currentCard?.fieldsForExisting?.fields_type || {};
  const updateFieldFormats = currentCard?.fieldsForExisting?.fields_formats || {};
  const updatePicklistMappings = currentCard?.fieldsForExisting?.picklistMapping || {};

  const getDataForMode = (mode: MappingMode) => ({
    fieldsMap: mode === 'update' ? updateFieldsMap : createFieldsMap,
    fieldsType: mode === 'update' ? updateFieldsType : createFieldsType,
    fieldFormats: mode === 'update' ? updateFieldFormats : createFieldFormats,
    picklistMappings: mode === 'update' ? updatePicklistMappings : createPicklistMappings,
  });

  const createFieldCount = Object.keys(createFieldsMap).length;
  const updateFieldCount = Object.keys(updateFieldsMap).length;

  // Regular field counts — exclude auto-added match fields (UA_INCOMING_*)
  // Used for display ("X fields" / "Not configured") and guards (create-before-update)
  const createRegularFieldCount = useMemo(
    () => Object.keys(createFieldsMap).filter((k) => !k.startsWith('UA_INCOMING_')).length,
    [createFieldsMap],
  );
  const updateRegularFieldCount = useMemo(
    () => Object.keys(updateFieldsMap).filter((k) => !k.startsWith('UA_INCOMING_')).length,
    [updateFieldsMap],
  );

  // Reactively close Update section if Create regular fields become empty
  // (e.g. card switch, card data reload, user clears all create fields)
  useEffect(() => {
    if (updateSectionOpen && createRegularFieldCount === 0) {
      setUpdateSectionOpen(false);
    }
  }, [createRegularFieldCount, updateSectionOpen, selectedCardId]);

  // ── Monday-specific: separate match fields from regular fields ──
  const { matchFields, regularFields, relationFields } = useMemo(() => {
    const match: CRMField[] = [];
    const regular: CRMField[] = [];
    // Relation fields hold a record id supplied by linking to a previous step,
    // not a value the user types — they're rendered as their own picker rows and
    // must stay out of the normal field list and the Add Field dialog. A group
    // member without isRelation (e.g. Pipedrive deal's person_name) is an
    // ordinary value field that happens to satisfy the group, so it stays in
    // regularFields and remains mappable as normal.
    const relation: CRMField[] = [];
    crmFields.forEach((f) => {
      if (f.isMatchField) match.push(f);
      else if (f.requiredOneOf && f.isRelation) relation.push(f);
      else regular.push(f);
    });
    return { matchFields: match, regularFields: regular, relationFields: relation };
  }, [crmFields]);

  /** Relation fields bucketed by their requiredOneOf key — one entry per OR-group. */
  const relationGroups = useMemo(() => {
    const groups = new Map<string, CRMField[]>();
    relationFields.forEach((f) => {
      const key = f.requiredOneOf as string;
      groups.set(key, [...(groups.get(key) ?? []), f]);
    });
    return Array.from(groups, ([key, groupFields]) => ({ key, fields: groupFields }));
  }, [relationFields]);

  /** Required rows the user fills in by hand (drives the "Add Required" button). */
  const requiredFieldTotal = useMemo(() => {
    return regularFields.filter((f) => f.required && !f.readOnly).length;
  }, [regularFields]);

  /** Each OR-group counts as one required row alongside the hand-filled ones. */
  const requiredTotal = requiredFieldTotal + relationGroups.length;

  const splitFields = useCallback((mappedNames: string[]) => {
    const req: string[] = [];
    const opt: string[] = [];
    mappedNames.forEach((name) => {
      // Skip match fields (they're rendered separately in Match Configuration section)
      if (crmFields.find((f) => f.apiName === name)?.isMatchField) return;
      // Relation fields are NOT skipped here. They're kept out of the Add Field
      // dialog (via regularFields) so the link picker is the way to set them, but
      // a mapping saved before they were grouped may already carry one as a plain
      // value — that row must stay visible and removable rather than vanish.
      // Safety: also skip UA_INCOMING_ fields even if not in current crmFields (stale saved data)
      if (name.startsWith('UA_INCOMING_')) return;
      const crm = regularFields.find((f) => f.apiName === name);
      if (crm?.required) req.push(name);
      else opt.push(name);
    });
    return { required: req, optional: opt };
  }, [crmFields, regularFields]);

  const createMappedNames = useMemo(() => Object.keys(createFieldsMap), [createFieldsMap]);
  const updateMappedNames = useMemo(() => Object.keys(updateFieldsMap), [updateFieldsMap]);
  const createSplit = useMemo(() => splitFields(createMappedNames), [createMappedNames, splitFields]);
  const updateSplit = useMemo(() => splitFields(updateMappedNames), [updateMappedNames, splitFields]);

  // Previous cards in order (for linking to previous modules).
  // Computed here — not below the early returns — to keep hook order stable.
  const previousCards = useMemo(() => {
    const currentIndex = allCards.findIndex((c) => c.cardId === selectedCardId);
    if (currentIndex <= 0) return [];
    return allCards.slice(0, currentIndex);
  }, [allCards, selectedCardId]);

  // Filter previous cards — exclude same module (can't link a module to itself)
  const linkableCards = useMemo(() => {
    if (!currentCard) return [];
    return previousCards.filter(
      (card) => card.saasFieldModuleName !== currentCard.saasFieldModuleName
    );
  }, [previousCards, currentCard]);

  // Which previous cards can actually be linked to this one, and through which
  // field id. getAdvanceLinkFieldIdForMMM is the same resolver handleLinkParentChange
  // validates with — precomputing it lets the picker offer only valid parents
  // instead of rejecting the choice with a toast after the fact.
  const [linkTargets, setLinkTargets] = useState<Record<string, string>>({});
  const currentCardModule = currentCard?.saasFieldModuleName;
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!currentCardModule || !saasModule?.getAdvanceLinkFieldIdForMMM) {
        if (!cancelled) setLinkTargets({});
        return;
      }
      const resolved: Record<string, string> = {};
      for (const card of linkableCards) {
        const fieldId = await saasModule
          .getAdvanceLinkFieldIdForMMM(card.saasFieldModuleName, currentCardModule)
          .catch(() => '');
        if (fieldId) resolved[card.cardId] = fieldId;
      }
      if (!cancelled) setLinkTargets(resolved);
    };
    void resolve();
    return () => { cancelled = true; };
  }, [linkableCards, currentCardModule, saasModule]);

  /**
   * An OR-group is satisfied either by a configured link that writes into one of
   * the group's fields (a Pipedrive lead linked to a Contacts card resolves
   * advanced_link_FieldId to "person_id"), or by the user mapping one of those
   * fields to a value directly — mappings authored before the group existed did
   * exactly that, and they are still valid payloads.
   */
  const isRelationSatisfied = useCallback(
    (group: { key: string; fields: CRMField[] }) => {
      const linkFieldId = currentCard?.advanced_link_FieldId;
      if (linkFieldId && currentCard?.advanced_link_parent?.length) {
        if (group.fields.some((f) => f.apiName === linkFieldId)) return true;
      }
      // Checked against every member of the group in crmFields, not just the
      // link-picker ones, so a plain value field that also satisfies it counts
      // (Pipedrive deal's person_name makes the backend create the person).
      return crmFields.some((f) => {
        if (f.requiredOneOf !== group.key) return false;
        const mapped = createFieldsMap[f.apiName];
        return typeof mapped === 'string' && mapped.trim() !== '';
      });
    },
    [currentCard?.advanced_link_FieldId, currentCard?.advanced_link_parent, createFieldsMap, crmFields]
  );

  // Settings flags
  const alwaysCreateNew = currentCard?.syncToExisting === false;
  const updateDisabled = currentCard?.updateDisabled ?? false;

  // Count configured settings for badge
  const settingsCount = useMemo(() => {
    let count = 0;
    if (alwaysCreateNew) count++;
    if (updateDisabled) count++;
    if ((currentCard?.advanced_link_parent?.length ?? 0) > 0) count++;
    return count;
  }, [alwaysCreateNew, updateDisabled, currentCard?.advanced_link_parent]);

  // ── Auto-add match fields to Create form when they're not already present (Monday-specific) ──
  useEffect(() => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    if (matchFields.length === 0) return;

    const missingMatchFields = matchFields.filter(
      (f) => !(f.apiName in createFieldsMap)
    );
    if (missingMatchFields.length === 0) return;

    // Add missing match fields with auto-selected first option
    const newFieldsMap = { ...createFieldsMap };
    const newFieldsType = { ...createFieldsType };
    missingMatchFields.forEach((f) => {
      // Auto-select first picklist value if available
      const autoValue = f.pickListValues?.[0]?.value || '';
      newFieldsMap[f.apiName] = autoValue;
      newFieldsType[f.apiName] = f.dataType || 'picklist';
    });

    updateCard(selectedBranchId, selectedCardId, {
      fields: newFieldsMap,
      fields_type: newFieldsType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchFields.length, selectedBranchId, selectedCardId]);

  // ── Handlers ──

  const handleFieldValueChange = (mode: MappingMode, fieldApiName: string, value: string) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    const { fieldsMap, fieldsType } = getDataForMode(mode);
    const crmField = crmFields.find((f) => f.apiName === fieldApiName);
    const newFieldsMap = { ...fieldsMap, [fieldApiName]: value };
    const newFieldsType = { ...fieldsType, [fieldApiName]: crmField?.dataType || 'text' };

    if (mode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: newFieldsMap,
          fields_type: newFieldsType,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, {
        fields: newFieldsMap,
        fields_type: newFieldsType,
      });
    }
  };

  const handleQuickFormatChange = (mode: MappingMode, fieldApiName: string, format: string) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    const { fieldsMap, fieldsType, fieldFormats } = getDataForMode(mode);
    const newFormats = {
      ...fieldFormats,
      [fieldApiName]: {
        '1': {
          quickFormat: format,
          filter: [{ type: 'exists', params: {} }],
          format: { type: format, params: {} },
        },
      },
    };

    if (mode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: fieldsMap,
          fields_type: fieldsType,
          fields_formats: newFormats,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, { fields_formats: newFormats });
    }
  };

  /**
   * Starting value for a freshly added field.
   *
   * Empty for everything except the module's email field, which is prefilled with
   * the dummy address the incoming default schema uses
   * (`<phone>@<appCode>.apps.ulgebra.com`) — FreshSales / Zendesk reject a new
   * record without an email, and a user mapping replaces the defaults wholesale
   * on the server. Editable like any other value; create mode only, since the
   * dummy is only meaningful when a record is being created.
   */
  const initialValueForField = (mode: MappingMode, fieldApiName: string): string => {
    if (mode !== 'create' || !currentCard) return '';
    const dummy = getDefaultDummyEmail(currentCard.saasFieldModuleName);
    return dummy?.field === fieldApiName ? dummy.value : '';
  };

  const handleAddField = (field: CRMField) => {
    if (!selectedBranchId || !selectedCardId) return;
    handleFieldValueChange(dialogMode, field.apiName, initialValueForField(dialogMode, field.apiName));
  };

  const handleBulkAdd = (mode: MappingMode, fieldsToAdd: CRMField[]) => {
    if (!selectedBranchId || !selectedCardId || !currentCard || fieldsToAdd.length === 0) return;
    const { fieldsMap, fieldsType } = getDataForMode(mode);

    // Build the complete new maps in one pass
    const newFieldsMap = { ...fieldsMap };
    const newFieldsType = { ...fieldsType };
    fieldsToAdd.forEach((f) => {
      newFieldsMap[f.apiName] = initialValueForField(mode, f.apiName);
      newFieldsType[f.apiName] = f.dataType || 'text';
    });

    if (mode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: newFieldsMap,
          fields_type: newFieldsType,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, {
        fields: newFieldsMap,
        fields_type: newFieldsType,
      });
    }
  };

  const handleBulkAddRequired = (mode: MappingMode) => {
    const mappedNames = mode === 'update' ? updateMappedNames : createMappedNames;
    const requiredFields = regularFields.filter(
      (f) => f.required && !f.readOnly && !mappedNames.includes(f.apiName)
    );
    handleBulkAdd(mode, requiredFields);
  };

  const handleBulkAddAll = (mode: MappingMode) => {
    const mappedNames = mode === 'update' ? updateMappedNames : createMappedNames;
    const writableFields = regularFields.filter(
      (f) => !f.readOnly && !mappedNames.includes(f.apiName)
    );
    handleBulkAdd(mode, writableFields);
  };

  const handleRemoveField = (mode: MappingMode, fieldApiName: string) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    const { fieldsMap, fieldsType } = getDataForMode(mode);
    const newFieldsMap = { ...fieldsMap };
    delete newFieldsMap[fieldApiName];
    const newFieldsType = { ...fieldsType };
    delete newFieldsType[fieldApiName];

    if (mode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: newFieldsMap,
          fields_type: newFieldsType,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, {
        fields: newFieldsMap,
        fields_type: newFieldsType,
      });
    }
  };

  const handleClearAllFields = (mode: MappingMode) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    if (mode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: {},
          fields_type: {},
          fields_formats: {},
          picklistMapping: {},
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, {
        fields: {},
        fields_type: {},
        fields_formats: {},
        picklistMapping: {},
      });
    }
  };

  const handlePicklistSave = (fieldApiName: string, mapping: PicklistMapping[]) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    const { fieldsMap, fieldsType, picklistMappings } = getDataForMode(dialogMode);
    const newMappings = { ...picklistMappings, [fieldApiName]: mapping };

    if (dialogMode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: fieldsMap,
          fields_type: fieldsType,
          picklistMapping: newMappings,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, { picklistMapping: newMappings });
    }
  };

  const handleAdvancedFormatSave = (
    fieldApiName: string,
    config: Record<string, FieldFormatConfig>
  ) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;
    const { fieldsMap, fieldsType, fieldFormats } = getDataForMode(dialogMode);
    const newFormats = { ...fieldFormats, [fieldApiName]: config };

    if (dialogMode === 'update') {
      updateCard(selectedBranchId, selectedCardId, {
        fieldsForExisting: {
          ...currentCard.fieldsForExisting,
          fields: fieldsMap,
          fields_type: fieldsType,
          fields_formats: newFormats,
          ct: Date.now(),
        },
      });
    } else {
      updateCard(selectedBranchId, selectedCardId, { fields_formats: newFormats });
    }
  };

  // ── Settings handlers ──

  const handleToggleAlwaysCreate = () => {
    if (!selectedBranchId || !selectedCardId) return;
    updateCard(selectedBranchId, selectedCardId, { syncToExisting: alwaysCreateNew });
  };

  const handleToggleUpdateDisabled = () => {
    if (!selectedBranchId || !selectedCardId) return;
    updateCard(selectedBranchId, selectedCardId, { updateDisabled: !updateDisabled });
  };

  const handleLinkParentChange = async (parentCardId: string) => {
    if (!selectedBranchId || !selectedCardId || !currentCard) return;

    // Detach this card from its previous parent's child list, if any.
    const previousParentId = currentCard.advanced_link_parent?.[0];
    if (previousParentId && previousParentId !== parentCardId) {
      const previousParent = allCards.find((c) => c.cardId === previousParentId);
      const siblings = previousParent?.advanced_link_child;
      if (siblings && siblings.length > 0) {
        updateCard(selectedBranchId, previousParentId, {
          advanced_link_child: siblings.filter((cid) => cid !== selectedCardId),
        });
      }
    }

    if (!parentCardId) {
      // Clearing the link
      updateCard(selectedBranchId, selectedCardId, {
        advanced_link_parent: [],
        advanced_link_FieldId: '',
      });
      return;
    }

    // Find parent card to get its module name
    const parentCard = allCards.find((c) => c.cardId === parentCardId);
    if (!parentCard) return;

    // Validate via getAdvanceLinkFieldIdForMMM (async — may fetch fields from CRM API)
    const fieldId = await saasModule?.getAdvanceLinkFieldIdForMMM?.(
      parentCard.saasFieldModuleName,
      currentCard.saasFieldModuleName
    ) ?? '';

    if (!fieldId) {
      toast.error(`${currentCard.saasFieldModuleName} cannot be linked to ${parentCard.saasFieldModuleName}.`);
      return;
    }

    // Child side: point to the parent and record the link field id.
    updateCard(selectedBranchId, selectedCardId, {
      advanced_link_parent: [parentCardId],
      advanced_link_FieldId: fieldId,
    });

    // Parent side: register this child. The server only caches the parent record
    // for child linking when advanced_link_child is non-empty (sms-functions.js).
    const existingChildren = parentCard.advanced_link_child ?? [];
    if (!existingChildren.includes(selectedCardId)) {
      updateCard(selectedBranchId, parentCardId, {
        advanced_link_child: [...existingChildren, selectedCardId],
      });
    }
  };

  // ── Resolve field references against event data ──
  const resolveFieldValue = (mappedValue: string, eventData: Record<string, unknown>): { resolved: string; hasUnresolved: boolean } => {
    let resolved = mappedValue;
    let hasUnresolved = false;
    const refRegex = /\$\{\{\{(.+?)\}\}\}/g;
    let match;
    while ((match = refRegex.exec(mappedValue)) !== null) {
      const path = match[1];
      const pathParts = path.replace(/^\$\./, '').split('.');
      let val: unknown = eventData;
      for (const part of pathParts) {
        if (val && typeof val === 'object') {
          val = (val as Record<string, unknown>)[part];
        } else {
          val = undefined;
          break;
        }
      }
      if (val !== undefined) {
        resolved = resolved.replace(match[0], String(val));
      } else {
        resolved = resolved.replace(match[0], '(not found)');
        hasUnresolved = true;
      }
    }
    return { resolved, hasUnresolved };
  };

  // ── Apply advanced format pipeline (fields_formats) to a value ──
  const applyFormatPipeline = (
    value: string,
    formatConfig: Record<string, FieldFormatConfig>
  ): string => {
    if (!value || !formatConfig || Object.keys(formatConfig).length === 0) return value;

    // Steps are keyed as "1", "2", "3" etc. Process in order.
    const stepKeys = Object.keys(formatConfig).sort((a, b) => Number(a) - Number(b));
    let currentValue = value;

    for (const stepKey of stepKeys) {
      const step = formatConfig[stepKey];
      if (!step) continue;

      // Check filter condition — first matching filter applies its format
      const filterDef = step.filter?.[0];
      const filterType = filterDef?.type || 'exists';
      const filterParams = (filterDef?.params || {}) as Record<string, string>;
      const filterMatch = evaluatePipelineFilter(currentValue, filterType, filterParams);

      if (filterMatch && step.format) {
        currentValue = applyPipelineFormat(
          currentValue,
          step.format.type,
          step.format.params as Record<string, string>
        );
      }
    }

    return currentValue;
  };

  /** Evaluate a pipeline filter condition */
  const evaluatePipelineFilter = (value: string, filterType: string, params: Record<string, string>): boolean => {
    const p1 = params.param1 || '';
    switch (filterType) {
      case 'exists': return value !== '' && value != null;
      case 'not_exists': return value === '' || value == null;
      case 'equals': return value === p1;
      case 'not_equals': return value !== p1;
      case 'contains': return value.includes(p1);
      case 'not_contains': return !value.includes(p1);
      case 'starts_with': return value.startsWith(p1);
      case 'not_starts_with': return !value.startsWith(p1);
      case 'ends_with': return value.endsWith(p1);
      case 'not_ends_with': return !value.endsWith(p1);
      case 'any_of': return p1.split(',').map((s) => s.trim()).includes(value);
      case 'greater_than': return Number(value) > Number(p1);
      case 'less_than': return Number(value) < Number(p1);
      case 'is_true': return value === 'true' || value === '1';
      case 'is_false': return value === 'false' || value === '0';
      default: return true;
    }
  };

  /** Apply a pipeline format transformation */
  const applyPipelineFormat = (value: string, formatType: string, params: Record<string, string>): string => {
    const p1 = params.param1 || '';
    const p2 = params.param2 || '';

    try {
      switch (formatType) {
        case 'parse_text': return value;
        case 'parse_int': return String(parseInt(value, 10) || 0);
        case 'parse_json': return JSON.stringify(JSON.parse(value));
        case 'parse_array': return JSON.stringify(value.split(',').map((s) => s.trim()));
        case 'capitalize': return value.charAt(0).toUpperCase() + value.slice(1);
        case 'lowercase': return value.toLowerCase();
        case 'titlecase': return value.replace(/\b\w/g, (c) => c.toUpperCase());
        case 'trim': return value.trim();
        case 'trim_all': return value.replace(/ /g, '');
        case 'replace': return p1 ? value.split(p1).join(p2) : value;
        case 'replace_value': return p1;
        case 'prefix': return p1 + value;
        case 'postfix': return value + p1;
        case 'default': return value || p1;
        case 'extract_email': { const m = value.match(/[\w.+-]+@[\w-]+\.[\w.]+/); return m ? m[0] : ''; }
        case 'extract_phone_number': { const m = value.match(/[+]?[\d\s()-]{7,}/); return m ? m[0].trim() : ''; }
        case 'extract_url': { const m = value.match(/https?:\/\/[^\s]+/); return m ? m[0] : ''; }
        case 'extract_number': { const m = value.match(/-?\d+(\.\d+)?/); return m ? m[0] : ''; }
        case 'split': { const parts = value.split(p1 || ','); return parts[parseInt(p2, 10) || 0] ?? ''; }
        case 'slice': { const s = parseInt(p1, 10) || 0; const e = p2 ? parseInt(p2, 10) : undefined; return value.slice(s, e); }
        case 'substr': { const s = parseInt(p1, 10) || 0; const l = p2 ? parseInt(p2, 10) : undefined; return l !== undefined ? value.substring(s, s + l) : value.substring(s); }
        case 'length': return String(value.length);
        case 'word_count': return String(value.trim() ? value.trim().split(/\s+/).length : 0);
        case 'math': {
          const num = parseFloat(value) || 0;
          const operand = parseFloat(p2) || 0;
          switch (p1) {
            case '+': case 'add': return String(num + operand);
            case '-': case 'sub': return String(num - operand);
            case '*': case 'mul': return String(num * operand);
            case '/': case 'div': return operand !== 0 ? String(num / operand) : 'Infinity';
            default: return String(num);
          }
        }
        case 'url_encode': return encodeURIComponent(value);
        case 'url_decode': return decodeURIComponent(value);

        // Date/Time formatting — param1 = timezone, param2 = format string
        case 'dateFormats':
        case 'timeFormats':
        case 'dateTimeFormats': {
          const timezone = p1;
          const format = p2;
          if (!format) return value;
          if (format === 'timestamp') {
            try {
              const d = new Date(value);
              if (isNaN(d.getTime())) return value;
              return String(d.getTime());
            } catch { return value; }
          }
          try {
            const d = new Date(value);
            if (isNaN(d.getTime())) return value;
            return formatDateTimeWithPattern(d, format, timezone);
          } catch { return value; }
        }

        default: return value;
      }
    } catch {
      return value;
    }
  };

  /**
   * Format a Date using a moment.js-style pattern string (e.g. DD-MM-YYYY, hh:mm A Z).
   * Uses Intl.DateTimeFormat for timezone conversion, then manual pattern replacement.
   */
  const formatDateTimeWithPattern = (date: Date, pattern: string, timezone?: string): string => {
    try {
      // Get date parts in the target timezone
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone || undefined,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      };
      const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
      const partMap: Record<string, string> = {};
      for (const p of parts) {
        partMap[p.type] = p.value;
      }

      const year = partMap.year || '';
      const month = partMap.month || '';
      const day = partMap.day || '';
      const hour24 = parseInt(partMap.hour || '0', 10);
      const minute = partMap.minute || '00';
      const second = partMap.second || '00';
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';

      // Month names
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const dayShort = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const mi = parseInt(month, 10) - 1;

      // Get day of week in target timezone
      const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone || undefined }));
      const dow = tzDate.getDay();
      const dayOfMonth = parseInt(day, 10);

      // Get timezone offset string
      let tzOffset = '';
      if (timezone) {
        try {
          const tzParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(date);
          const tzPart = tzParts.find(p => p.type === 'timeZoneName');
          if (tzPart) tzOffset = tzPart.value;
        } catch { /* */ }
        if (!tzOffset) {
          try {
            const tzParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longOffset' }).formatToParts(date);
            const tzPart = tzParts.find(p => p.type === 'timeZoneName');
            if (tzPart) tzOffset = tzPart.value;
          } catch { /* */ }
        }
      }

      // Apply pattern replacements (order matters — longer tokens first)
      let result = pattern;
      result = result.replace(/YYYY/g, year);
      result = result.replace(/YY/g, year.slice(-2));
      result = result.replace(/MMMM/g, monthNames[mi] || '');
      result = result.replace(/MMM/g, monthShort[mi] || '');
      result = result.replace(/MM/g, month);
      result = result.replace(/(?<![HhDdYM])M(?![MoYDdhms])/g, String(parseInt(month, 10)));
      result = result.replace(/DD/g, day);
      result = result.replace(/(?<![MDYHhms])D(?![MDYHhms])/g, String(dayOfMonth));
      result = result.replace(/dddd/g, dayNames[dow] || '');
      result = result.replace(/ddd/g, dayShort[dow] || '');
      result = result.replace(/HH/g, String(hour24).padStart(2, '0'));
      result = result.replace(/(?<![Hh])H(?![Hh])/g, String(hour24));
      result = result.replace(/hh/g, String(hour12).padStart(2, '0'));
      result = result.replace(/(?<![Hh])h(?![Hh])/g, String(hour12));
      result = result.replace(/mm/g, minute);
      result = result.replace(/ss/g, second);
      result = result.replace(/SS/g, second);
      result = result.replace(/A/g, ampm);
      result = result.replace(/a/g, ampm.toLowerCase());
      result = result.replace(/Z/g, tzOffset);

      return result;
    } catch {
      return date.toISOString();
    }
  };

  // ── Coerce value based on field type ──
  const coerceValue = (value: string, dataType: string): unknown => {
    if (!value || value === '(not found)' || value === '(empty)') return value;
    switch (dataType) {
      case 'parse_int':
      case 'integer':
      case 'number':
        return Number(value) || 0;
      case 'parse_json':
      case 'json':
        try { return JSON.parse(value); } catch { return value; }
      case 'boolean':
        return value === 'true' || value === '1';
      default:
        return value;
    }
  };

  /**
   * Convert a field value based on the CRM system's expected data type.
   * Matches legacy `convertFieldBySysType` — handles date/datetime, boolean, phone, etc.
   */
  const convertFieldBySysType = (
    currentVal: unknown,
    dataType: string,
    saasId?: string
  ): unknown => {
    try {
      if (currentVal == null || currentVal === '') return currentVal;

      const dt = dataType?.toLowerCase() || '';
      const valStr = String(currentVal);

      // Date/DateTime fields — format based on CRM type
      if (['date_time', 'datetime', 'date'].includes(dt)) {
        const d = new Date(valStr);
        if (isNaN(d.getTime())) return currentVal;

        if (saasId?.includes('zoho')) {
          if (dt === 'date') {
            return d.toISOString().split('T')[0]; // "YYYY-MM-DD"
          }
          // DateTime → "2023-12-23T09:15:37+05:30"
          const offset = d.getTimezoneOffset();
          const absOffset = Math.abs(offset);
          const sign = offset < 0 ? '+' : '-';
          const tzStr = sign + String(Math.floor(absOffset / 60)).padStart(2, '0') + ':' + String(absOffset % 60).padStart(2, '0');
          return d.toISOString().split('.')[0] + tzStr;
        }
        if (saasId?.includes('hubspot') || saasId?.includes('zohodesk')) {
          return d.toISOString();
        }
        if (saasId?.includes('monday')) {
          const temp = d.toISOString().split('T');
          return { date: temp[0], time: temp[1]?.split('.')[0] || '00:00:00' };
        }
        // Default — return ISO string
        return d.toISOString();
      }

      // Boolean
      if (dt === 'boolean') {
        return valStr === 'true' || valStr === '1' || currentVal === true;
      }

      // Phone/Mobile — remove spaces
      if (['phone', 'mobile'].includes(dt)) {
        return valStr.replace(/ /g, '');
      }

      return currentVal;
    } catch {
      return currentVal;
    }
  };

  // ── Validate field mappings for a given mode ──
  const validateFields = (mode: MappingMode): ValidationResult => {
    const { fieldsMap } = getDataForMode(mode);
    const fieldResults: FieldResult[] = [];
    let errorCount = 0;
    let resolvedCount = 0;

    for (const [fieldApiName, mappedValue] of Object.entries(fieldsMap)) {
      const crm = crmFields.find((f) => f.apiName === fieldApiName);
      const isRequired = crm?.required ?? false;
      const label = crm?.displayLabel || fieldApiName;

      if (!mappedValue) {
        if (isRequired) {
          fieldResults.push({ fieldApiName, fieldLabel: label, required: isRequired, mappedValue: '', resolvedValue: '', status: 'error', issue: 'Required field is empty' });
          errorCount++;
        } else {
          fieldResults.push({ fieldApiName, fieldLabel: label, required: isRequired, mappedValue: '', resolvedValue: '', status: 'empty' });
        }
        continue;
      }

      if (selectedEventData) {
        const { resolved, hasUnresolved } = resolveFieldValue(mappedValue, selectedEventData as Record<string, unknown>);
        if (hasUnresolved) {
          fieldResults.push({ fieldApiName, fieldLabel: label, required: isRequired, mappedValue, resolvedValue: resolved, status: 'error', issue: 'Could not resolve field reference' });
          errorCount++;
        } else {
          fieldResults.push({ fieldApiName, fieldLabel: label, required: isRequired, mappedValue, resolvedValue: resolved, status: 'ok' });
          resolvedCount++;
        }
      } else {
        fieldResults.push({ fieldApiName, fieldLabel: label, required: isRequired, mappedValue, resolvedValue: mappedValue, status: 'ok' });
        resolvedCount++;
      }
    }

    // OR-group requirements (e.g. a Pipedrive lead needs person_id OR
    // organization_id). These aren't in fieldsMap — they're satisfied by the
    // card's link — so they're checked separately, and only on Create.
    if (mode === 'create') {
      for (const group of relationGroups) {
        if (isRelationSatisfied(group)) continue;
        const label = relationGroupLabel(group);
        fieldResults.push({
          fieldApiName: group.fields.map((f) => f.apiName).join(' | '),
          fieldLabel: `Linked record (${label})`,
          required: true,
          mappedValue: '',
          resolvedValue: '',
          status: 'error',
          issue: `Link this card to a previous ${label} step`,
        });
        errorCount++;
      }
    }

    return {
      passed: errorCount === 0,
      totalFields: fieldResults.length,
      resolvedCount,
      errorCount,
      fields: fieldResults,
    };
  };

  // ── Build resolved payload from validation result (applies format pipeline + CRM type conversion) ──
  const buildResolvedPayload = (
    validation: ValidationResult,
    fieldsType: Record<string, string>,
    fieldFormats?: Record<string, Record<string, FieldFormatConfig>>
  ): Record<string, unknown> => {
    const data: Record<string, unknown> = {};
    const saasId = saasModule?.id || '';

    for (const field of validation.fields) {
      if (field.status === 'ok' && field.resolvedValue) {
        let resolvedValue = field.resolvedValue;

        // 1. Apply advanced format pipeline if configured for this field
        const formatConfig = fieldFormats?.[field.fieldApiName];
        const hasFormatPipeline = formatConfig && Object.keys(formatConfig).length > 0;
        if (hasFormatPipeline) {
          resolvedValue = applyFormatPipeline(resolvedValue, formatConfig);
        }

        // 2. Get CRM field data type for system-specific conversion
        const crmField = crmFields.find((f) => f.apiName === field.fieldApiName);
        const crmDataType = crmField?.dataType || '';

        // 3. Apply CRM-specific type conversion ONLY if no format pipeline was configured
        //    (if user explicitly set a format pipeline, respect their chosen output format)
        let finalValue: unknown = resolvedValue;
        if (crmDataType && !hasFormatPipeline) {
          finalValue = convertFieldBySysType(resolvedValue, crmDataType, saasId);
        }

        // 4. Apply quick format type coercion (parse_int, parse_json, etc.)
        const quickFormatType = fieldsType[field.fieldApiName] || 'text';
        if (typeof finalValue === 'string') {
          finalValue = coerceValue(finalValue, quickFormatType);
        }

        data[field.fieldApiName] = finalValue;
      }
    }
    return data;
  };

  // ── Parse record ID from a URL (or raw ID fallback) ──
  const parseRecordId = (urlOrId: string): string | null => {
    const trimmed = urlOrId.trim();
    if (!trimmed) return null;
    // If it looks like a URL, use the SaaS module's parser
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      if (saasModule) {
        const parsed = saasModule.parseEntityIdFromURL(trimmed);
        if (parsed) return parsed;
      }
      // Generic fallback: extract last non-empty path segment
      try {
        const path = trimmed.split('?')[0].replace(/\/+$/, '');
        const segments = path.split('/');
        const last = segments[segments.length - 1];
        if (last && last.length > 0) return last;
      } catch { /* */ }
      return null;
    }
    // Fallback: treat as raw ID
    return trimmed;
  };

  // ── Build a create payload for an arbitrary card (used for linked parents).
  // Same pipeline as buildResolvedPayload minus the CRM-type conversion, which
  // needs the parent module's field definitions (not loaded here); the format
  // pipeline and quick-format coercion still apply. ──
  const buildCardCreatePayload = (card: MMMCard): Record<string, unknown> => {
    const data: Record<string, unknown> = {};
    for (const [apiName, mappedValue] of Object.entries(card.fields || {})) {
      if (!mappedValue || apiName.startsWith('UA_INCOMING_')) continue;

      let resolved = mappedValue;
      if (selectedEventData) {
        const r = resolveFieldValue(mappedValue, selectedEventData as Record<string, unknown>);
        if (r.hasUnresolved) continue;
        resolved = r.resolved;
      }

      const formatConfig = card.fields_formats?.[apiName];
      let finalValue: unknown =
        formatConfig && Object.keys(formatConfig).length > 0
          ? applyFormatPipeline(resolved, formatConfig)
          : resolved;
      if (typeof finalValue === 'string') {
        finalValue = coerceValue(finalValue, card.fields_type?.[apiName] || 'text');
      }
      data[apiName] = finalValue;
    }
    return data;
  };

  // ── Resolve the "Link to Previous Module" parent for a test run.
  // Reuse the parent record a test already created this session; otherwise
  // create the parent from its own create-mapping and remember it. ──
  const resolveParentForTest = async (): Promise<{
    parentRecordId?: string;
    parentModule?: string;
    parentError?: string;
  }> => {
    const parentCardId = currentCard?.advanced_link_parent?.[0];
    if (!parentCardId || !selectedBranchId || !saasModule) return {};
    // Only for SaaS whose createRecord consumes the parent link (Pipedrive).
    // Others ignore the extra args, so creating a parent here would just
    // leave a side-effect record in the user's CRM without linking anything.
    if (!saasModule.supportsLinkedParentOnCreate) return {};

    const parentCard = allCards.find((c) => c.cardId === parentCardId);
    if (!parentCard) return {};

    const parentModule = parentCard.saasFieldModuleName;
    const cacheKey = `${selectedBranchId}:${parentCard.cardId}`;
    const cached = testCreatedRecords[cacheKey];
    if (cached) {
      return { parentRecordId: cached.recordId, parentModule };
    }

    try {
      const parentPayload = buildCardCreatePayload(parentCard);
      if (Object.keys(parentPayload).length === 0) {
        return { parentError: `Linked module "${parentCard.displayLabel || parentModule}" has no resolvable create fields — test it first.` };
      }
      const newParentId = await saasModule.createRecord(parentModule, parentPayload);
      if (!newParentId) {
        return { parentError: `Could not create linked "${parentCard.displayLabel || parentModule}" record` };
      }
      testCreatedRecords[cacheKey] = { module: parentModule, recordId: newParentId };
      return { parentRecordId: newParentId, parentModule };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'create failed';
      return { parentError: `Linked "${parentCard.displayLabel || parentModule}" record creation failed: ${msg}` };
    }
  };

  // ── Test: Validate + API test for both Create and Update ──
  const handleTestMapping = async () => {
    if (!selectedBranchId) return;

    if (createRegularFieldCount === 0 && updateRegularFieldCount === 0) {
      toast.error('Map at least one field before testing.');
      return;
    }

    // Guard: if update fields exist, create must be configured first
    if (updateRegularFieldCount > 0 && createRegularFieldCount === 0 && !alwaysCreateNew) {
      toast.error('Please configure the "Create New Record" mapping first before testing update mapping.');
      return;
    }

    // Guard: an unsatisfied OR-group link would be rejected by the CRM with an
    // opaque API error, so name the missing link instead of letting it 400.
    if (createFieldCount > 0) {
      const missingLink = relationGroups.find((g) => !isRelationSatisfied(g));
      if (missingLink) {
        toast.error(`Link this card to a previous ${relationGroupLabel(missingLink)} step before testing.`);
        return;
      }
    }

    setTestLoading(true);

    try {
      const formResults: FormTestResult[] = [];
      const moduleName = currentCard?.saasFieldModuleName || '';
      let createdRecordId: string | undefined;

      // ── Determine which forms to test ──
      const testCreate = createFieldCount > 0;
      const testUpdate = updateFieldCount > 0 && !alwaysCreateNew;

      // ── Test Create ──
      if (testCreate) {
        const validation = validateFields('create');
        let api: ApiResult | null = null;

        if (validation.passed && saasModule && moduleName) {
          const resolvedData = buildResolvedPayload(validation, createFieldsType, createFieldFormats);

          // Linked parent (Link to Previous Module): reuse this session's
          // tested parent record, or create the parent first
          const { parentRecordId, parentModule, parentError } = await resolveParentForTest();

          if (parentError) {
            api = {
              attempted: true, success: false, error: parentError,
              requestPayload: resolvedData,
            };
          } else {
            try {
              const recordId = await saasModule.createRecord(moduleName, resolvedData, parentRecordId, parentModule);
              const rawResponse = saasModule.lastRawApiResponse;

              if (recordId) {
                createdRecordId = recordId;
                if (selectedBranchId && selectedCardId) {
                  testCreatedRecords[`${selectedBranchId}:${selectedCardId}`] = { module: moduleName, recordId };
                }
                let recordUrl: string | undefined;
                try { recordUrl = saasModule.getRecordWebURL(moduleName, recordId); } catch { /* */ }

                api = {
                  attempted: true, success: true, recordId, recordUrl,
                  requestPayload: resolvedData,
                  response: rawResponse ?? { success: true, id: recordId, module: moduleName },
                };
              } else {
                api = {
                  attempted: true, success: false,
                  error: 'API returned no record ID — create may have failed',
                  requestPayload: resolvedData,
                  response: rawResponse ?? { success: false, id: null, module: moduleName },
                };
              }
            } catch (err) {
              const rawResponse = saasModule.lastRawApiResponse;
              const errorMsg = err instanceof Error ? err.message : 'API call failed';
              api = {
                attempted: true, success: false, error: errorMsg,
                requestPayload: resolvedData,
                response: rawResponse ?? { success: false, error: errorMsg, module: moduleName },
              };
            }
          }
        } else if (!validation.passed) {
          api = { attempted: false, success: false, error: 'Skipped — validation errors' };
        } else if (!saasModule) {
          api = { attempted: false, success: false, error: 'SaaS module not connected' };
        }

        formResults.push({ mode: 'create', label: 'Create New Record', validation, api });
      }

      // ── Test Update ──
      if (testUpdate) {
        const validation = validateFields('update');
        let api: ApiResult | null = null;

        if (validation.passed && saasModule && moduleName) {
          const resolvedData = buildResolvedPayload(validation, updateFieldsType, updateFieldFormats);
          // Use created record ID from Create test, or parse from user-provided URL
          const targetRecordId = createdRecordId || parseRecordId(testRecordUrl);

          if (targetRecordId) {
            try {
              const success = await saasModule.updateRecord(moduleName, targetRecordId, resolvedData);
              const rawResponse = saasModule.lastRawApiResponse;

              let recordUrl: string | undefined;
              try { recordUrl = saasModule.getRecordWebURL(moduleName, targetRecordId); } catch { /* */ }

              api = {
                attempted: true, success, recordId: targetRecordId, recordUrl,
                requestPayload: resolvedData,
                response: rawResponse ?? { success, updated: success, id: targetRecordId, module: moduleName },
              };
            } catch (err) {
              const rawResponse = saasModule.lastRawApiResponse;
              const errorMsg = err instanceof Error ? err.message : 'Update API call failed';
              api = {
                attempted: true, success: false, error: errorMsg,
                requestPayload: resolvedData,
                response: rawResponse ?? { success: false, error: errorMsg, module: moduleName },
              };
            }
          } else {
            // No record ID available — prompt user for a URL
            api = {
              attempted: false, success: false,
              error: 'Record URL needed to test update',
              requestPayload: resolvedData,
            };
            formResults.push({ mode: 'update', label: 'Update Existing Record', validation, api, needsRecordUrl: true });
          }
        } else if (!validation.passed) {
          api = { attempted: false, success: false, error: 'Skipped — validation errors' };
        } else if (!saasModule) {
          api = { attempted: false, success: false, error: 'SaaS module not connected' };
        }

        if (!formResults.some((f) => f.mode === 'update')) {
          formResults.push({ mode: 'update', label: 'Update Existing Record', validation, api });
        }
      }

      const overallPassed = formResults.every(
        (f) => f.validation.passed && (f.api === null || f.api.success)
      );
      setTestResultData({ forms: formResults, overallPassed });
      setTestResultOpen(true);
    } catch {
      setTestResultData({
        forms: [{ mode: 'create', label: 'Create New Record', validation: { passed: false, totalFields: 0, resolvedCount: 0, errorCount: 0, fields: [] }, api: { attempted: false, success: false, error: 'Unexpected error during test' } }],
        overallPassed: false,
      });
      setTestResultOpen(true);
    } finally {
      setTestLoading(false);
    }
  };

  // ── Save: Validate both forms first, then persist ──
  const handleSave = async () => {
    if (!selectedBranchId) return;

    if (createRegularFieldCount === 0 && updateRegularFieldCount === 0) {
      toast.error('Map at least one field before saving.');
      return;
    }

    // Guard: if update fields exist, create must be configured first
    if (updateRegularFieldCount > 0 && createRegularFieldCount === 0 && !alwaysCreateNew) {
      toast.error('Please configure the "Create New Record" mapping first before saving update mapping.');
      return;
    }

    // Validate both forms
    const formResults: FormTestResult[] = [];

    if (createFieldCount > 0) {
      const validation = validateFields('create');
      formResults.push({ mode: 'create', label: 'Create New Record', validation, api: null });
    }
    if (updateFieldCount > 0 && !alwaysCreateNew) {
      const validation = validateFields('update');
      formResults.push({ mode: 'update', label: 'Update Existing Record', validation, api: null });
    }

    const hasErrors = formResults.some((f) =>
      f.validation.fields.some((field) => field.required && field.status === 'error')
    );

    if (hasErrors) {
      setTestResultData({ forms: formResults, overallPassed: false });
      setTestResultOpen(true);
      return;
    }

    saveBranch(selectedBranchId);
  };

  const isSaving = selectedBranchId ? saving[selectedBranchId] : false;

  if (!fieldMappingPanelOpen) return null;

  // Single-module apps: show module selector when no card exists yet
  if (!currentCard && !isMultiModule && selectedBranchId) {
    const supportedModules = saasModuleHook?.getSupportedModules() || [];
    const handleModuleSelect = (moduleValue: string, moduleLabel: string) => {
      const cardId = `__single_${Date.now()}`;
      const newCard: MMMCard = {
        cardId,
        saasFieldModuleName: moduleValue,
        displayLabel: moduleLabel,
        orderOf: 0,
        fields: {},
        fields_type: {},
        ct: Date.now(),
      };
      addCard(selectedBranchId, newCard);
      selectCard(cardId);
    };

    return (
      <div data-testid="fm-field-mapping-panel" className="absolute right-0 top-0 z-20 flex h-full w-[420px] flex-col border-l border-border bg-background shadow-xl animate-slide-in-right">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Select Module</h2>
            <p className="text-[11px] text-muted-foreground">Choose a CRM module for field mapping</p>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={() => setFieldMappingPanelOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Module list */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {supportedModules.map((mod) => (
              <button
                key={mod.value}
                onClick={() => handleModuleSelect(mod.value, mod.label)}
                className="flex w-full items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors hover:bg-muted/50 hover:border-primary/30"
              >
                <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground">{mod.label}</p>
                  <p className="text-[10px] text-muted-foreground">Create {mod.label}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard) return null;

  // Quick format helper
  const getQuickFormat = (formats: Record<string, Record<string, FieldFormatConfig>>, fieldApiName: string): string => {
    const fmts = formats[fieldApiName];
    if (!fmts) return 'parse_text';
    const firstStep = Object.values(fmts)[0];
    return firstStep?.quickFormat || firstStep?.format?.type || 'parse_text';
  };

  // ── Render a field row ──
  const renderFieldRow = (mode: MappingMode, fieldApiName: string, index: number) => {
    const { fieldsMap, fieldsType, fieldFormats, picklistMappings } = getDataForMode(mode);
    const crmField = crmFields.find((f) => f.apiName === fieldApiName);
    const hasPicklist = crmField?.pickListValues != null && crmField.pickListValues.length > 0;

    return (
      <div
        key={fieldApiName}
        className="animate-fade-in-up"
        style={{ animationDelay: `${Math.min(index * 25, 250)}ms` }}
      >
        <FieldRow
          fieldApiName={fieldApiName}
          fieldDisplayLabel={crmField?.displayLabel || fieldApiName}
          dataType={crmField?.dataType || fieldsType[fieldApiName] || 'text'}
          required={crmField?.required || false}
          mappedValue={fieldsMap[fieldApiName] || ''}
          quickFormat={getQuickFormat(fieldFormats, fieldApiName)}
          hasPicklist={hasPicklist}
          pickListValues={crmField?.pickListValues || []}
          picklistMapping={picklistMappings[fieldApiName] || []}
          formatConfig={fieldFormats[fieldApiName] || {}}
          availableEventFields={eventFieldPaths}
          onValueChange={(val) => handleFieldValueChange(mode, fieldApiName, val)}
          onQuickFormatChange={(fmt) => handleQuickFormatChange(mode, fieldApiName, fmt)}
          onPicklistClick={() => {
            setDialogMode(mode);
            if (crmField) setPicklistDialogField(crmField);
          }}
          onAdvancedClick={() => {
            setDialogMode(mode);
            setAdvancedDialogField(fieldApiName);
          }}
          onRemove={() => handleRemoveField(mode, fieldApiName)}
          simpleMode={!isMultiModule}
          hint={
            fieldsMap[fieldApiName]?.includes(DUMMY_EMAIL_DOMAIN_SUFFIX)
              ? `${getTPADisplayName(appCodeTPA)} doesn't send an email address, so we filled in a dummy one — you can change it.`
              : undefined
          }
        />
      </div>
    );
  };

  // ── Render match fields section (Monday-specific: phone/email column selectors) ──
  const renderMatchFieldsSection = (mode: MappingMode) => {
    if (matchFields.length === 0) return null;
    // Match fields are only shown on Create form (hideOnUpdate)
    if (mode === 'update') return null;
    const { fieldsMap } = getDataForMode(mode);

    return (
      <div className="mb-3 pb-3 border-b border-border/30">
        <p className="mb-2 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          Match Configuration
        </p>
        <div className="space-y-2.5">
          {matchFields.map((mf) => (
            <div key={mf.apiName}>
              <div className="flex items-center gap-1 mb-1">
                <ShieldCheck className="h-2.5 w-2.5 shrink-0 text-primary/60" />
                <span className="text-[11px] font-medium text-foreground" title={mf.displayLabel}>
                  {mf.displayLabel}
                </span>
              </div>
              <Select
                value={fieldsMap[mf.apiName] || ''}
                onValueChange={(val) => handleFieldValueChange(mode, mf.apiName, val)}
                options={(mf.pickListValues || []).map((opt) => ({
                  value: opt.value,
                  label: `${opt.label} (${mf.matchConfig?.type || 'column'})`,
                }))}
                placeholder={`Select ${mf.matchConfig?.label || 'column'}...`}
                searchable
                triggerClassName="h-7 text-xs px-2 py-1 rounded-md"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Render OR-group link pickers (e.g. Pipedrive lead → Person or Organization) ──
  // These satisfy a required record link that the CRM can't express as a plain
  // required field, so they live in the Required section rather than behind the
  // Module Settings gear.
  const renderRelationSection = (mode: MappingMode) => {
    // The link is a property of the card itself, so it only applies to Create.
    if (mode !== 'create' || relationGroups.length === 0) return null;

    return (
      <>
        {relationGroups.map((group) => {
          const satisfied = isRelationSatisfied(group);
          const label = relationGroupLabel(group);
          const options = linkableCards.filter((card) =>
            group.fields.some((f) => f.apiName === linkTargets[card.cardId])
          );
          // Plain value fields that also satisfy this group — offered as a way
          // out when no linkable step exists (Pipedrive deal's person_name).
          const altLabel = crmFields
            .filter((f) => f.requiredOneOf === group.key && !f.isRelation)
            .map((f) => f.displayLabel || f.apiName)
            .join(' or ');

          return (
            <div key={group.key}>
              <div className="mb-1 flex items-center gap-1">
                <Link2 className="h-2.5 w-2.5 shrink-0 text-blue-500" />
                <span className="text-[11px] font-medium text-foreground">Linked record</span>
                <span className="text-[7px] font-bold text-destructive">*</span>
                <span className="text-[10px] text-muted-foreground/70">· {label}</span>
              </div>

              {options.length === 0 ? (
                <p className="rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[9px] leading-relaxed text-amber-700 dark:text-amber-400">
                  Add a {label} module card in an earlier step, then link it here
                  {altLabel ? `, or map ${altLabel} below` : ''}.
                </p>
              ) : (
                <>
                  <Select
                    value={currentCard.advanced_link_parent?.[0] || '__none__'}
                    onValueChange={(val) => handleLinkParentChange(val === '__none__' ? '' : val)}
                    placeholder="None"
                    triggerClassName={`h-7 px-2 text-[10px] ${satisfied ? '' : 'border-destructive/50'}`}
                    options={[
                      { value: '__none__', label: 'None' },
                      ...options.map((card) => {
                        const stepIndex = allCards.findIndex((c) => c.cardId === card.cardId);
                        return {
                          value: card.cardId,
                          label: `Step ${stepIndex + 1} · ${card.displayLabel || card.saasFieldModuleName}`,
                        };
                      }),
                    ]}
                  />
                  {!satisfied && (
                    <p className="mt-1 text-[9px] leading-relaxed text-destructive/80">
                      This record can’t be created unless it links to a {label}
                      {altLabel ? `, or maps ${altLabel}` : ''}.
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // ── Render field list with required/optional split ──
  const renderFieldList = (
    mode: MappingMode,
    split: { required: string[]; optional: string[] },
    mappedNames: string[],
    fieldCount: number,
    optCollapsed: boolean,
    setOptCollapsed: (v: boolean) => void,
  ) => (
    <>
      {/* Match fields — Monday-specific column selectors (Create only) */}
      {renderMatchFieldsSection(mode)}

      {/* Loading */}
      {fieldsLoading && (
        <div className="py-4 text-center">
          <div className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" />
          <p className="text-[10px] text-muted-foreground">Loading fields...</p>
        </div>
      )}

      {/* Empty State */}
      {!fieldsLoading && fieldCount === 0 && (
        <div className="py-3 text-center">
          <p className="text-[10px] text-muted-foreground mb-3">
            {mode === 'create'
              ? `Map event fields to ${currentCard.saasFieldModuleName} fields.`
              : `Override specific fields when updating existing records.`}
          </p>
          <div className="flex items-center justify-center gap-2">
            {/* Counts only hand-filled fields — OR-group links are configured
                through their own picker, not added as rows. */}
            {requiredFieldTotal > 0 && mode === 'create' && (
              <Button
                variant="default"
                size="sm"
                className="gap-1 text-[10px] h-6 px-2"
                onClick={() => handleBulkAddRequired(mode)}
              >
                <Plus className="h-2.5 w-2.5" />
                Add Required ({requiredFieldTotal})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-[10px] h-6 px-2"
              onClick={() => handleBulkAddAll(mode)}
            >
              <Plus className="h-2.5 w-2.5" />
              Add All
            </Button>
          </div>
        </div>
      )}

      {/* Required Fields */}
      {!fieldsLoading && (split.required.length > 0 || (mode === 'create' && relationGroups.length > 0)) && (() => {
        const satisfiedGroups = mode === 'create'
          ? relationGroups.filter(isRelationSatisfied).length
          : 0;
        const satisfiedCount = split.required.length + satisfiedGroups;
        return (
          <div>
            <p className="mb-2 text-[9px] font-semibold text-muted-foreground/60">
              Required
              {mode === 'create' && requiredTotal > 0 && (
                <span className={`ml-1 ${
                  satisfiedCount >= requiredTotal ? 'text-emerald-600' : ''
                }`}>
                  ({satisfiedCount}/{requiredTotal})
                </span>
              )}
            </p>
            <div className="space-y-3">
              {split.required.map((name, i) => renderFieldRow(mode, name, i))}
              {renderRelationSection(mode)}
            </div>
          </div>
        );
      })()}

      {/* Optional Fields */}
      {!fieldsLoading && split.optional.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <button
              onClick={() => setOptCollapsed(!optCollapsed)}
              className="flex items-center gap-1.5 cursor-pointer"
            >
              {optCollapsed ? (
                <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
              ) : (
                <ChevronDown className="h-3 w-3 text-muted-foreground/40" />
              )}
              <span className="text-[9px] font-semibold text-muted-foreground/60">
                Optional ({split.optional.length})
              </span>
            </button>
          </div>
          {!optCollapsed && (
            <div className="space-y-3">
              {split.optional.map((name, i) =>
                renderFieldRow(mode, name, split.required.length + i)
              )}
            </div>
          )}
        </div>
      )}

      {/* Flat list fallback — filter out match/UA_INCOMING_ fields (rendered separately above) */}
      {!fieldsLoading && fieldCount > 0 && split.required.length === 0 && split.optional.length === 0 && (() => {
        const regularNames = mappedNames.filter((n) => !n.startsWith('UA_INCOMING_'));
        return regularNames.length > 0 ? (
          <div className="space-y-3">
            {regularNames.map((name, i) => renderFieldRow(mode, name, i))}
          </div>
        ) : null;
      })()}

      {/* Add Field button */}
      {!fieldsLoading && (
        <button
          onClick={() => {
            setDialogMode(mode);
            setShowFieldAddDialog(true);
          }}
          className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border py-1.5 text-[11px] text-muted-foreground/50 transition-colors hover:border-primary/40 hover:text-primary"
        >
          <Plus className="h-3 w-3" />
          Add Field
        </button>
      )}
    </>
  );

  // Dialog data
  const dialogData = getDataForMode(dialogMode);

  return (
    <div data-testid="fm-field-mapping-panel" className="absolute right-0 top-0 z-50 flex h-full w-[540px] flex-col border-l border-border bg-background shadow-xl animate-slide-in-right">
      {/* ─── Header ─── */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: '#0d9488' }}>
          <Database className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[13px] font-semibold text-foreground leading-tight">
            {currentCard.displayLabel || currentCard.saasFieldModuleName}
          </h3>
          <p className="text-[10px] text-muted-foreground leading-tight">
            {currentMapping?.name || 'Branch'} · Step {cardStepIndex || 1} · {currentCard.saasFieldModuleName}
          </p>
        </div>

        {/* Settings gear icon with popover — only for multi-module apps */}
        {isMultiModule && (
          <div className="relative shrink-0" ref={settingsPopoverRef}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => (settingsOpen && settingsVisible) ? closeSettings() : openSettings()}
              className={`relative ${settingsOpen ? 'bg-accent' : ''}`}
              title="Module Settings"
            >
              <Settings className="h-3.5 w-3.5" />
              {settingsCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-primary px-0.5 text-[8px] font-bold text-primary-foreground">
                  {settingsCount}
                </span>
              )}
            </Button>

            {settingsOpen && (
              <div className={`absolute right-0 top-full mt-1.5 z-[60] w-[320px] rounded-xl border border-border bg-popover shadow-xl origin-top-right transition-all duration-150 ease-out ${
                settingsVisible
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-1'
              }`}>
                <div className="flex items-center justify-between border-b border-border/50 px-3 py-2">
                  <span className="text-[11px] font-semibold text-foreground">Module Settings</span>
                  <button onClick={closeSettings} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="p-3 space-y-2.5 max-h-[400px] overflow-y-auto">
                  {/* Always Create New */}
                  <label className="flex items-start gap-2.5 rounded-lg border border-border/50 bg-muted/20 p-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
                    <div className="mt-0.5 shrink-0">
                      <div
                        className={`relative h-4 w-7 rounded-full transition-colors ${
                          alwaysCreateNew ? 'bg-primary' : 'bg-muted-foreground/20'
                        }`}
                        onClick={(e) => { e.preventDefault(); handleToggleAlwaysCreate(); }}
                      >
                        <div
                          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                            alwaysCreateNew ? 'translate-x-3.5' : 'translate-x-0.5'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1" onClick={(e) => { e.preventDefault(); handleToggleAlwaysCreate(); }}>
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="h-3 w-3 text-blue-500" />
                        <span className="text-[10px] font-semibold text-foreground">Always Create New</span>
                      </div>
                      <p className="text-[9px] text-muted-foreground/70 mt-0.5 leading-relaxed">
                        Skip duplicate check — always create a new record, never update existing.
                      </p>
                    </div>
                  </label>

                  {/* Pause Update Mapping */}
                  {updateRegularFieldCount > 0 && (
                    <label className="flex items-start gap-2.5 rounded-lg border border-border/50 bg-muted/20 p-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
                      <div className="mt-0.5 shrink-0">
                        <div
                          className={`relative h-4 w-7 rounded-full transition-colors ${
                            updateDisabled ? 'bg-amber-500' : 'bg-muted-foreground/20'
                          }`}
                          onClick={(e) => { e.preventDefault(); handleToggleUpdateDisabled(); }}
                        >
                          <div
                            className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                              updateDisabled ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1" onClick={(e) => { e.preventDefault(); handleToggleUpdateDisabled(); }}>
                        <div className="flex items-center gap-1.5">
                          <PauseCircle className="h-3 w-3 text-amber-500" />
                          <span className="text-[10px] font-semibold text-foreground">Pause Update Mapping</span>
                        </div>
                        <p className="text-[9px] text-muted-foreground/70 mt-0.5 leading-relaxed">
                          Temporarily disable the update field mapping without deleting it.
                        </p>
                      </div>
                    </label>
                  )}

                  {/* Link to Previous Module */}
                  {linkableCards.length > 0 && (
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Link2 className="h-3 w-3 text-blue-500" />
                        <span className="text-[10px] font-semibold text-foreground">Link to Previous Module</span>
                      </div>
                      <p className="text-[9px] text-muted-foreground/70 mb-2 leading-relaxed">
                        Link this record to a supported module from a previous step.
                      </p>
                      <Select
                        value={currentCard.advanced_link_parent?.[0] || '__none__'}
                        onValueChange={(val) => handleLinkParentChange(val === '__none__' ? '' : val)}
                        placeholder="None"
                        triggerClassName="h-7 text-[10px] px-2"
                        options={[
                          { value: '__none__', label: 'None' },
                          ...linkableCards.map((card) => {
                            const stepIndex = allCards.findIndex((c) => c.cardId === card.cardId);
                            return {
                              value: card.cardId,
                              label: `Step ${stepIndex + 1} · ${card.displayLabel || card.saasFieldModuleName}`,
                            };
                          }),
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setFieldMappingPanelOpen(false)}
          className="shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* ─── Scrollable content ─── */}
      <div className="flex-1 overflow-y-auto">

        {/* ─── How it works ─── */}
        <div className="px-4 py-2.5 border-b border-border/50 bg-muted/20">
          <p className="text-[10px] leading-relaxed text-muted-foreground">
            {!isMultiModule ? (
              <>Map event data to CRM fields. A new record will be created for each event.</>
            ) : isEventApp ? (
              <>
                Existing records are matched by <span className="font-medium text-foreground">email</span> &amp; <span className="font-medium text-foreground">phone</span>.
                {' '}If a match is found, the existing record is updated. Otherwise, a new record is created.
              </>
            ) : (
              <>
                Existing records are matched by <span className="font-medium text-foreground">phone number</span>.
                {' '}If a match is found, the existing record is updated. Otherwise, a new record is created.
              </>
            )}
          </p>
        </div>

        {/* ─── Create New — collapsible, optional ─── */}
        <div className="mt-2 mx-3 rounded-lg border border-border/50 overflow-hidden">
          <div className="flex items-center bg-muted/60 hover:bg-muted transition-colors">
            <button
              onClick={() => { setCreateSectionOpen(!createSectionOpen); setUpdateSectionOpen(false); }}
              className="flex flex-1 items-center gap-2 px-3 py-2.5 text-left min-w-0"
            >
              <div className="shrink-0">
                {createSectionOpen ? (
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                )}
              </div>
              <FilePlus className={`h-3.5 w-3.5 shrink-0 ${createSectionOpen ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="min-w-0 flex-1">
                <span className="text-[11px] font-semibold text-foreground">
                  Create New Record
                </span>
                <p className="text-[9px] leading-snug text-muted-foreground mt-0.5">
                  {alwaysCreateNew
                    ? 'Always creates a new record — duplicate check is skipped.'
                    : 'Map event data to create a new record when no match is found.'}
                </p>
              </div>
            </button>
            <span className={`shrink-0 text-[9px] font-medium ${createRegularFieldCount > 0 ? 'text-primary' : 'text-muted-foreground/40'} ${createRegularFieldCount > 0 ? '' : 'mr-3'}`}>
              {alwaysCreateNew
                ? `Always · ${createRegularFieldCount} field${createRegularFieldCount !== 1 ? 's' : ''}`
                : createRegularFieldCount > 0
                  ? `${createRegularFieldCount} field${createRegularFieldCount !== 1 ? 's' : ''}`
                  : 'Not configured'}
            </span>
            {createRegularFieldCount > 0 && (
              <button
                onClick={() => handleClearAllFields('create')}
                className="shrink-0 mx-2 rounded p-1 text-muted-foreground/30 hover:bg-destructive/10 hover:text-destructive transition-colors"
                title="Clear all create fields"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="accordion-content" data-open={createSectionOpen}>
            <div>
              <div className="px-4 pt-3 pb-3 space-y-3">
                {renderFieldList(
                  'create', createSplit, createMappedNames, createFieldCount,
                  createOptionalCollapsed, setCreateOptionalCollapsed,
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Update Existing — collapsible, optional (hidden for non-MMM or when Always Create New is on) ─── */}
        {isMultiModule && !alwaysCreateNew && (
          <div className="mt-2 mx-3 rounded-lg border border-border/50 overflow-hidden">
            <div className="flex items-center bg-muted/60 hover:bg-muted transition-colors">
              <button
                onClick={() => {
                  // Guard: create mapping must be configured (with real fields, not just match fields)
                  // before opening update mapping
                  if (!updateSectionOpen && createRegularFieldCount === 0) {
                    toast.error('Please configure and save the "Create New Record" mapping first before configuring update mapping.');
                    return;
                  }
                  setUpdateSectionOpen(!updateSectionOpen);
                  setCreateSectionOpen(false);
                }}
                className="flex flex-1 items-center gap-2 px-3 py-2.5 text-left min-w-0"
              >
                <div className="shrink-0">
                  {updateSectionOpen ? (
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                  )}
                </div>
                <RefreshCw className={`h-3.5 w-3.5 shrink-0 ${updateSectionOpen ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-semibold text-foreground">
                    Update Existing Record
                  </span>
                  <p className="text-[9px] leading-snug text-muted-foreground mt-0.5">
                    Map event data to update an existing record when a match is found.
                  </p>
                </div>
              </button>
              <span className={`shrink-0 text-[9px] font-medium ${updateRegularFieldCount > 0 ? 'text-primary' : 'text-muted-foreground/40'} ${updateRegularFieldCount > 0 ? '' : 'mr-3'}`}>
                {updateDisabled
                  ? 'Paused'
                  : updateRegularFieldCount > 0
                    ? `${updateRegularFieldCount} field${updateRegularFieldCount !== 1 ? 's' : ''}`
                    : 'Not configured'}
              </span>
              {updateRegularFieldCount > 0 && (
                <button
                  onClick={() => handleClearAllFields('update')}
                  className="shrink-0 mx-2 rounded p-1 text-muted-foreground/30 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  title="Clear all update fields"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>

            <div className="accordion-content" data-open={updateSectionOpen}>
              <div>
                <div className={`px-4 pt-3 pb-3 space-y-3 ${updateDisabled ? 'opacity-40 pointer-events-none' : ''}`}>
                  {renderFieldList(
                    'update', updateSplit, updateMappedNames, updateFieldCount,
                    updateOptionalCollapsed, setUpdateOptionalCollapsed,
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Note: MMM_DISALLOWING_DIRECT_UPDATE is defined in the original but never actually used
           to hide the Update section — Monday always shows both Create and Update field mapping */}

      </div>

      {/* ─── Test Result Panel ─── */}
      {testResultData && testResultOpen && (
        <div className="border-t border-border bg-muted/20 max-h-[45vh] overflow-y-auto scrollbar-thin">
          {/* Result Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 border-b border-border/50 bg-muted/30 backdrop-blur-sm">
            <div className="flex items-center gap-1.5">
              {testResultData.overallPassed ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-destructive" />
              )}
              <span className="text-[11px] font-semibold text-foreground">Test Result</span>
              <span className="text-[9px] text-muted-foreground">
                ({testResultData.forms.length} form{testResultData.forms.length !== 1 ? 's' : ''})
              </span>
            </div>
            <button
              onClick={() => setTestResultOpen(false)}
              className="rounded p-0.5 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>

          {/* Per-form results */}
          {testResultData.forms.map((form) => (
            <FormTestResultSection
              key={form.mode}
              form={form}
              needsRecordUrl={form.needsRecordUrl}
              recordUrl={form.needsRecordUrl ? testRecordUrl : undefined}
              onRecordUrlChange={form.needsRecordUrl ? setTestRecordUrl : undefined}
              onRetry={form.needsRecordUrl ? handleTestMapping : undefined}
              retrying={testLoading}
            />
          ))}
        </div>
      )}

      {/* ─── Footer ─── */}
      <div className="border-t border-border px-4 flex items-center h-[45px]">
        <div className="flex flex-1 items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-[10px] h-7 px-2"
              onClick={handleTestMapping}
              disabled={testLoading || (createFieldCount === 0 && updateFieldCount === 0)}
              title={!selectedEventData ? 'Select an event from sidebar first' : 'Test field mapping with sample data'}
            >
              <PlayCircle className="h-3 w-3" />
              {testLoading ? 'Testing...' : 'Test'}
            </Button>
          </div>
          <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-7 text-[11px]">
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* ─── Dialogs ─── */}
      <FieldAddDialog
        open={showFieldAddDialog}
        onClose={() => setShowFieldAddDialog(false)}
        onSelect={handleAddField}
        availableFields={regularFields}
        existingFieldNames={dialogMode === 'update' ? updateMappedNames : createMappedNames}
      />

      {picklistDialogField && (
        <PicklistMappingDialog
          open={!!picklistDialogField}
          onClose={() => setPicklistDialogField(null)}
          fieldName={picklistDialogField.apiName}
          fieldLabel={picklistDialogField.displayLabel}
          picklistOptions={
            picklistDialogField.pickListValues?.map((p) => ({
              value: p.value,
              label: p.label,
            })) || []
          }
          existingMapping={dialogData.picklistMappings[picklistDialogField.apiName] || []}
          onSave={(mapping) => handlePicklistSave(picklistDialogField.apiName, mapping)}
          saasCategory={getSaaSPrettyName(appCodeSAAS)}
        />
      )}

      {advancedDialogField && (
        <AdvancedFormatDialog
          open={!!advancedDialogField}
          onClose={() => setAdvancedDialogField(null)}
          fieldName={advancedDialogField}
          fieldLabel={
            crmFields.find((f) => f.apiName === advancedDialogField)?.displayLabel ||
            advancedDialogField
          }
          existingConfig={dialogData.fieldFormats[advancedDialogField] || {}}
          onSave={(config) => handleAdvancedFormatSave(advancedDialogField, config)}
          sampleValue={(() => {
            const raw = dialogData.fieldsMap[advancedDialogField] || '';
            if (!raw || !selectedEventData) return raw;
            let resolved = raw;
            const refRegex = /\$\{\{\{(.+?)\}\}\}/g;
            let m;
            while ((m = refRegex.exec(raw)) !== null) {
              const path = m[1];
              const pathParts = path.replace(/^\$\./, '').split('.');
              let val: unknown = selectedEventData;
              for (const part of pathParts) {
                if (val && typeof val === 'object') {
                  val = (val as Record<string, unknown>)[part];
                } else { val = undefined; break; }
              }
              resolved = resolved.replace(m[0], val !== undefined ? String(val) : '');
            }
            return resolved;
          })()}
        />
      )}
    </div>
  );
}
